import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

import { LoginCredentials, AuthResponse, AuthState, AuthUser, UserRole } from '../models/auth_model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Inyectamos PLATFORM_ID para saber si estamos en el servidor o en el navegador
  private platformId = inject(PLATFORM_ID);
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly API_URL = `${environment.baseUrl}/api`;

  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  // Estado reactivo con signals
  private authState = signal<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false
  });

  // Computed signals para acceso a propiedades específicas
  public currentUser = computed(() => this.authState().user);
  public isAuthenticated = computed(() => this.authState().isAuthenticated);
  public userRole = computed(() => this.authState().user?.role ?? null);

  constructor() {
    // IMPORTANTE: Solo cargar desde localStorage si estamos en el cliente
    if (isPlatformBrowser(this.platformId)) {
      this.loadAuthState();
    }
  }

  /**
   * Obtiene las opciones HTTP con o sin autenticación
   */
  private getHttpOptions(includeAuth = false, withCredentials = false) {
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };

    if (includeAuth && this.authState().token) {
      headers['Authorization'] = `Bearer ${this.authState().token}`;
    }

    return {
      headers: new HttpHeaders(headers),
      withCredentials: withCredentials
    };
  }

  /**
   * Realiza el login y guarda el estado de autenticación
   */
  login(credentials: LoginCredentials): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(
      `${this.API_URL}/login`,
      credentials,
      this.getHttpOptions(false, false)
    ).pipe(
      tap(response => {
        this.saveAuthState(response.user, response.access_token);
      }),
      catchError(error => {
        console.error('Error en login:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Guarda el estado de autenticación
   */
  private saveAuthState(user: AuthUser, token: string): void {
    if (user && token && (user.role === undefined || user.role === null)) {
      const tokenRole = this.getRoleFromToken(token);
      if (tokenRole !== null) {
        user.role = tokenRole;
      }
    }

    // Solo persistimos en localStorage si estamos en el navegador
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }

    this.authState.set({
      user,
      token,
      isAuthenticated: true
    });
  }

  /**
   * Extrae el rol del payload del token JWT
   */
  private getRoleFromToken(token: string): UserRole | null {
    try {
      const payloadPart = token.split('.')[1];
      if (!payloadPart) return null;

      const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/');

      // Solución para atob: En el servidor usamos Buffer, en el cliente window.atob
      let decodedPayload: string;
      if (isPlatformBrowser(this.platformId)) {
        decodedPayload = window.atob(base64);
      } else {
        decodedPayload = Buffer.from(base64, 'base64').toString('binary');
      }

      const jsonPayload = decodeURIComponent(decodedPayload.split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      const payload = JSON.parse(jsonPayload);
      const role = payload.role ?? payload.roles ?? payload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

      return role !== undefined ? (Number(role) as UserRole) : null;
    } catch (e) {
      console.error('Error decodificando token:', e);
      return null;
    }
  }

  /**
   * Carga el estado de autenticación
   */
  private loadAuthState(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const token = localStorage.getItem(this.TOKEN_KEY);
    const userStr = localStorage.getItem(this.USER_KEY);

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr) as AuthUser;

        if ((user.role === undefined || user.role === null) && token) {
          const tokenRole = this.getRoleFromToken(token);
          if (tokenRole !== null) {
            user.role = tokenRole;
            localStorage.setItem(this.USER_KEY, JSON.stringify(user));
          }
        }

        this.authState.set({
          user,
          token,
          isAuthenticated: true
        });
      } catch (error) {
        this.clearAuthState();
      }
    }
  }

  /**
   * Limpia el estado de autenticación
   */
  private clearAuthState(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.USER_KEY);
    }

    this.authState.set({
      user: null,
      token: null,
      isAuthenticated: false
    });
  }

  logout(): void {
    this.clearAuthState();
    this.router.navigate(['/login']);
  }

  hasAnyRole(roles: UserRole[]): boolean {
    const currentRole = this.userRole();
    if (currentRole === null || currentRole === undefined) return false;
    return roles.some(role => role == currentRole);
  }

  isAdmin(): boolean {
    return this.hasRole(UserRole.ADMIN);
  }

  hasRole(role: UserRole): boolean {
    const userRole = this.userRole();
    return userRole == role;
  }

  getToken(): string | null {
    return this.authState().token;
  }
}